import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { NavGroupDef, NavGroupId, NavItemDef } from '../../../navigation/menu.ts'
import { Icon } from '../Icon'
import { IconButton } from '../Button'
import BrandLogo from '../../BrandLogo'
import { NavGroup } from './NavGroup'
import { NavSearch, NavSearchResults } from './NavSearch'
import './nav.css'

export type NavOpenMode = 'multi' | 'single'

const SCROLL_KEY = 'sidebarScrollPos'
function readScroll(): number {
  try { return parseInt(sessionStorage.getItem(SCROLL_KEY) ?? '0') || 0 } catch { return 0 }
}
function writeScroll(v: number): void {
  try { sessionStorage.setItem(SCROLL_KEY, String(v)) } catch { /* sessionStorage non disponibile */ }
}

/**
 * Contenuto del navigation drawer: logo, ricerca nel menu, gruppi a tendina, area Account.
 * Lo stesso contenuto sta sia nella expanded rail standard (large/extra-large) sia nella modal expanded rail.
 */
export function NavigationDrawer({
  groups, accountItems, openIds, onToggleGroup, activeGroupId, activeItemId,
  mode, onModeChange, onCollapseAll, onLogout, onNavigate, onClose, onCollapse, fabSlot,
}: {
  groups: NavGroupDef[]
  accountItems: readonly NavItemDef[]
  openIds: ReadonlySet<NavGroupId>
  onToggleGroup: (id: NavGroupId) => void
  activeGroupId: NavGroupId | null
  activeItemId: string | null
  mode: NavOpenMode
  onModeChange: (m: NavOpenMode) => void
  onCollapseAll: () => void
  onLogout: () => void
  onNavigate?: (() => void) | undefined
  onClose?: (() => void) | undefined
  /** Solo nella expanded rail standard: comprime il menu nella collapsed rail. */
  onCollapse?: (() => void) | undefined
  /** FAB menu esteso (azioni rapide) nello slot FAB in testa alla expanded rail standard. */
  fabSlot?: ReactNode | undefined
}) {
  const navRef = useRef<HTMLElement>(null)
  const [query, setQuery] = useState('')
  const searching = query.trim().length > 0

  // Posizione di scorrimento salvata a ogni scroll (sessionStorage sempre in try/catch). Mentre si ripristina
  // non si salva: il contenuto puo' essere ancora corto (gruppi in animazione) e il valore verrebbe troncato.
  const restoring = useRef(false)
  useEffect(() => {
    const nav = navRef.current; if (!nav) return
    const save = () => { if (!restoring.current && nav.clientHeight > 0) writeScroll(nav.scrollTop) }
    nav.addEventListener('scroll', save, { passive: true })
    return () => nav.removeEventListener('scroll', save)
  }, [])

  // La voce attiva deve restare visibile: al cambio di rotta, all'apertura di un gruppo e quando il
  // drawer modale (nascosto finché è chiuso) diventa visibile.
  const openKey = [...openIds].sort().join(',')
  const [navVisible, setNavVisible] = useState(false)
  useEffect(() => {
    const nav = navRef.current; if (!nav || typeof ResizeObserver === 'undefined') { setNavVisible(true); return }
    const ro = new ResizeObserver(() => setNavVisible(nav.clientHeight > 0))
    ro.observe(nav)
    return () => ro.disconnect()
  }, [])
  // Ripristino: al montaggio, quando il drawer modale si riapre (un <dialog> chiuso azzera lo scroll) e a ogni
  // cambio di rotta. Si riprova per qualche frame finche' i gruppi aperti non hanno raggiunto l'altezza piena.
  useLayoutEffect(() => {
    if (!navVisible || searching) return
    const nav = navRef.current; if (!nav) return
    const target = readScroll()
    let frames = 0, raf = 0
    restoring.current = true
    const apply = () => {
      if (Math.abs(nav.scrollTop - target) > 1) nav.scrollTop = target
      if (Math.abs(nav.scrollTop - target) > 1 && ++frames < 30) raf = requestAnimationFrame(apply)
      else restoring.current = false
    }
    apply()
    return () => { cancelAnimationFrame(raf); restoring.current = false }
  }, [navVisible, searching, activeItemId])
  useEffect(() => {
    if (searching || !navVisible) return
    const nav = navRef.current; if (!nav) return
    const el = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (!el || el.closest('[inert]')) return
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Il pannello del gruppo si anima in apertura: si aspetta la fine per misurare la posizione vera.
    const t = window.setTimeout(() => el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' }), reduce ? 0 : 250)
    return () => window.clearTimeout(t)
  }, [activeItemId, openKey, searching, navVisible])

  function handleNavigate() {
    if (navRef.current) writeScroll(navRef.current.scrollTop)
    setQuery('')
    onNavigate?.()
  }

  return (
    <div className="md-drawer">
      <div className="md-drawer__head">
        <BrandLogo height={36} />
        {onClose && <IconButton icon="close" aria-label="Chiudi menu" onClick={onClose} />}
        {onCollapse && !onClose && <IconButton icon="menu_open" aria-label="Comprimi menu" onClick={onCollapse} />}
      </div>
      {fabSlot}
      <NavSearch groups={groups} value={query} onChange={setQuery} onNavigate={handleNavigate} />

      <nav className="md-drawer__nav" aria-label="Menu principale" ref={navRef}>
        {searching ? (
          <NavSearchResults groups={groups} query={query} activeItemId={activeItemId} onNavigate={handleNavigate} />
        ) : (
          groups.map(g => (
            <NavGroup
              key={g.id}
              group={g}
              open={openIds.has(g.id)}
              hasActive={g.id === activeGroupId}
              activeItemId={activeItemId}
              onToggle={() => onToggleGroup(g.id)}
              onNavigate={handleNavigate}
            />
          ))
        )}
      </nav>

      <div className="md-drawer__foot" role="group" aria-label="Strumenti del menu">
        {onClose && (
          // Modal expanded rail: "Sicurezza account" ed "Esci" in una riga compatta.
          // Nella rail standard stanno nel menu dell'avatar dell'app bar.
          <div className="md-drawer__account">
            {accountItems.map(item => (
              <Link
                key={item.id}
                to={item.to}
                className={'md-drawer__tool md-state' + (item.id === activeItemId ? ' is-active' : '')}
                aria-current={item.id === activeItemId ? 'page' : undefined}
                onClick={handleNavigate}
              >
                <Icon name={item.icon} size={18} /> {item.label}
              </Link>
            ))}
            <button type="button" className="md-drawer__tool md-drawer__tool--danger md-state" onClick={onLogout}>
              <Icon name="logout" size={18} /> Esci
            </button>
          </div>
        )}
        <div className="md-drawer__tools">
          <IconButton
            icon="unfold_less"
            size="sm"
            aria-label="Comprimi tutto"
            onClick={onCollapseAll}
          />
          <IconButton
            icon="view_agenda"
            size="sm"
            aria-label="Un gruppo alla volta" tooltip="Un gruppo alla volta: tiene aperto un solo gruppo"
            selected={mode === 'single'}
            onClick={() => onModeChange(mode === 'single' ? 'multi' : 'single')}
          />
          <div className="md-version">Equal CRM v2.5.0</div>
        </div>
      </div>
    </div>
  )
}
